import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import LocaleRedirectModal from 'components/Common/Modals/LocaleRedirectModal/LocaleRedirectModal'
import ModalContext from 'contexts/ModalContext'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import config from 'constants/config'
import { requiresLocaleConflictConfirmation, resolveLocale } from 'i18n/util/locale'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { SET_REQUEST_LOCALE } from 'actions/actionConstants'
import { syncUserDetails } from 'actions/AuthActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'

import {
  getLanguagePreferenceFromAuth,
  updateCustomerLanguagePreferenceFromAuth,
} from 'api/auth'

export const ACCOUNT_LOCALE_REDIRECT_STORAGE_KEY = 'accountLocaleRedirect'

/**
 * Reconciles the authenticated customer language preference with the URL locale
 * after hydration. Locale changes load the matching browser bundle; explicit URL
 * locales remain the active page-language signal, and conflicts are resolved
 * through a one-per-session confirmation modal. Locales in the same language
 * family, such as en-AU and en-US, keep the page language and save it on the
 * account without a modal. zh-* still prompts. On a clean URL, a same-family account
 * locale does not redirect away from the server-resolved locale.
 */
export default function AuthBackedLocalePreferenceSync() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  // `resolveLocaleConflict` can be triggered again while the modal is still open,
  // so keep the in-flight promise to avoid stacking duplicate modals.
  const localeRedirectModalPromiseRef = useRef<Promise<unknown> | null>(null)
  // The guard below holds for the rest of the session and the effect re-runs on every
  // route change, so report a rejected redirect once rather than on each page.
  const hasReportedRejectedRedirectRef = useRef(false)

  const { languageOptions } = useLocaleUrlOptions()

  const location = useLocation()
  const urlLocation = useMemo(() => UrlLocation.fromLocation(location), [location])
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const accountLocale = useAppSelector(state => state.auth.account.language)
  const ssrLocale = useAppSelector(state => state.geo.locale)

  const urlLocale = useMemo(
    () => resolveLocale(urlLocation.locale, languageOptions),
    [urlLocation.locale, languageOptions],
  )
  const accountLocaleState = useMemo(
    () => resolveLocale(accountLocale, languageOptions),
    [accountLocale, languageOptions],
  )

  const navigateToLocale = useCallback(
    (locale: string) => {
      const redirect = urlLocation.getRedirectToLocale({
        targetLocale: locale,
        languageOptions,
      })
      const targetUrl = urlLocation.withRedirect(redirect)
      if (!targetUrl.equals(urlLocation)) {
        window.location.assign(targetUrl.toString())
      }
    },
    [urlLocation, languageOptions],
  )

  const resolveLocaleConflict = useCallback(
    async(authLocale: string) => {
      if (!urlLocale.full) return

      const keepPageLanguage = async() => {
        try {
          if (!urlLocation.locale || !urlLocale.full) return
          dispatch(syncUserDetails({ language: urlLocale.full }))
          await updateCustomerLanguagePreferenceFromAuth(urlLocale.full)
        } catch(error) {
          reportClientError(error)
        }
      }

      if (!requiresLocaleConflictConfirmation(urlLocale.full, authLocale)) {
        await keepPageLanguage()
        return
      }

      if (localeRedirectModalPromiseRef.current) {
        await localeRedirectModalPromiseRef.current
        return
      }

      const switchToAccountLanguage = () => {
        try {
          setLanguageCookie(authLocale)
          navigateToLocale(authLocale)
        } catch(error) {
          reportClientError(error)
        }
      }

      const modalPromise = Promise.resolve(showModal(
        <LocaleRedirectModal
          urlLocale={urlLocale.full}
          accountLocale={authLocale}
          onKeepPageLanguage={keepPageLanguage}
          onSwitchToAccountLanguage={switchToAccountLanguage}
        />,
      )).finally(() => {
        if (localeRedirectModalPromiseRef.current === modalPromise) {
          localeRedirectModalPromiseRef.current = null
        }
      })

      localeRedirectModalPromiseRef.current = modalPromise
      await modalPromise
    },
    [navigateToLocale, urlLocation.locale, urlLocale.full, showModal, dispatch],
  )

  useEffect(() => {
    if (!config.MULTI_LANGUAGE_ENABLED) return
    if (isSpoofed) return

    if (urlLocale.full && isomorphicSessionStorage.get(ACCOUNT_LOCALE_REDIRECT_STORAGE_KEY) === urlLocale.full) {
      // The redirect to the account locale landed, so a later one may run again.
      isomorphicSessionStorage.remove(ACCOUNT_LOCALE_REDIRECT_STORAGE_KEY)
    }

    const abortController = new AbortController()
    const { signal } = abortController

    async function run() {
      if (signal.aborted) return
      if (!isLoggedIn) return

      if (!accountLocale) {
        // `undefined` means the read failed, so a saved language may exist. Skip rather than
        // risk overwriting it; the next page load tries again.
        const freshAccountLocale = await getLanguagePreferenceFromAuth(signal).catch(() => undefined)
        if (signal.aborted || freshAccountLocale === undefined) return

        const freshAccount = resolveLocale(freshAccountLocale, languageOptions)

        if (freshAccount.full) {
          if (freshAccount.normalised !== urlLocale.normalised && urlLocale.full) {
            await resolveLocaleConflict(freshAccount.full)
          }
        } else if (urlLocale.full) {
          // Persist the page locale (e.g. negotiated from the browser) so comms can
          // send emails in the customer's language.
          try {
            await updateCustomerLanguagePreferenceFromAuth(urlLocale.full)
          } catch(error) {
            reportClientError(error)
            return
          }
          if (signal.aborted) return

          dispatch(syncUserDetails({ language: urlLocale.full }))
        }
      } else if (accountLocaleState.full) {
        if (urlLocale.full) {
          if (accountLocaleState.normalised !== urlLocale.normalised) {
            await resolveLocaleConflict(accountLocaleState.full)
          }
        } else {
          if (ssrLocale && ssrLocale !== accountLocaleState.full) {
            if (!requiresLocaleConflictConfirmation(ssrLocale, accountLocaleState.full)) return

            // Back on a clean URL after redirecting to this locale means the server sent us
            // back: it can't serve the locale to this request, e.g. it sees a stale auth
            // cookie and treats a staff-only locale as unavailable. Redirecting again loops.
            if (isomorphicSessionStorage.get(ACCOUNT_LOCALE_REDIRECT_STORAGE_KEY) === accountLocaleState.full) {
              if (!hasReportedRejectedRedirectRef.current) {
                hasReportedRejectedRedirectRef.current = true
                reportClientError(new Error(`Server rejected the redirect to account locale ${accountLocaleState.full}`))
              }
              return
            }

            isomorphicSessionStorage.set(ACCOUNT_LOCALE_REDIRECT_STORAGE_KEY, accountLocaleState.full)
            setLanguageCookie(accountLocaleState.full)
            navigateToLocale(accountLocaleState.full)
          } else {
            dispatch({ type: SET_REQUEST_LOCALE, locale: accountLocaleState.full })
          }
        }
      }
    }

    run()

    return () => {
      abortController.abort()
    }
  }, [
    dispatch,
    isSpoofed,
    isLoggedIn,
    languageOptions,
    accountLocale,
    urlLocale,
    accountLocaleState,
    resolveLocaleConflict,
    ssrLocale,
    navigateToLocale,
  ])

  return null
}
